// AssemblyWizard.jsx — "Assemblies & trace": the estimator's order of work.
//
//   1. Sheets: mark what each sheet is — assembly legend / floor plan (with its wall height) / reflected
//      ceiling plan / skip. Suggested from the title blocks. This ONE list drives everything: the assembly
//      reader reads only the legend sheets, Trace runs on exactly the floor plans + RCPs marked here.
//      (settings.sheetRoles — shared with Trace settings in the viewer)
//   2. Read the legend sheets → a list of types (tag, name, build-up), each with its own number. Tick the
//      ones to trace, and say which symbol the plans draw wall tags in.
//   3. Trace options (walls, doors, windows, ceilings).
//   4. Trace → walls are drawn on the floor plans per wall-type tag (doors left out, windows kept in the
//      run and deducted by area), ceilings are drawn room by room on the RCPs — every room, typed by its
//      tag/callout, else by the grid drawn in it (2'×4', 2'×2', none = drywall), else "Untagged ceiling".
//
// Pure UI: reading pages and running the trace are done by the callbacks the viewer passes in.
import React, { useEffect, useMemo, useState } from "react";
import { gridOfText } from "../lib/takeoff/engine/ceilingGrid";

const KIND_STYLE = {
  wall: "bg-orange-900/50 text-orange-300",
  ceiling: "bg-sky-900/50 text-sky-300",
  floor: "bg-gray-700 text-gray-300",
  roof: "bg-gray-700 text-gray-300",
  other: "bg-gray-700 text-gray-300",
};
const uid = () => Math.random().toString(36).slice(2, 10);

// ── tracing animation: a little plan drawing itself, a scan line, the steps so far and a progress bar ──
const TRACE_CSS = `
@keyframes tw-draw { from { stroke-dashoffset: var(--len); } to { stroke-dashoffset: 0; } }
@keyframes tw-scan { 0% { transform: translateY(-8px); opacity: 0; } 10% { opacity: .9; } 90% { opacity: .9; } 100% { transform: translateY(150px); opacity: 0; } }
@keyframes tw-pop { 0%, 60% { transform: scale(0); opacity: 0; } 80% { transform: scale(1.35); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
@keyframes tw-fill { 0%, 55% { opacity: 0; } 100% { opacity: .18; } }
@keyframes tw-stripes { from { background-position: 0 0; } to { background-position: 28px 0; } }
.tw-line { stroke-dasharray: var(--len); stroke-dashoffset: var(--len); animation: tw-draw 1.1s ease-in-out forwards; }
.tw-loop { animation-iteration-count: infinite; }
`;
const PLAN_WALLS = [
  // [x1, y1, x2, y2, delay s]  — a small floor plan, drawn wall by wall
  [10, 10, 230, 10, 0],
  [230, 10, 230, 140, 0.35],
  [230, 140, 10, 140, 0.7],
  [10, 140, 10, 10, 1.05],
  [95, 10, 95, 62, 1.4],
  [95, 88, 95, 140, 1.55],
  [95, 75, 230, 75, 1.8],
  [160, 75, 160, 140, 2.1],
];
const PLAN_DOORS = [
  [95, 75, 2.5],
  [160, 108, 2.7],
  [196, 75, 2.9],
];
function TraceAnimation({ running, log }) {
  const pct = running.total
    ? Math.min(100, Math.round((running.done / running.total) * 100))
    : 0;
  // restart the drawing for each new sheet so it keeps moving on long traces
  const cycle = `${running.done}-${(log || []).length}`;
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-950/85 backdrop-blur-sm">
      <style>{TRACE_CSS}</style>
      <div className="w-full max-w-md rounded-2xl border border-orange-500/30 bg-gray-900 p-5 shadow-2xl">
        <div className="relative mx-auto mb-4 h-[150px] w-[240px] overflow-hidden rounded-lg border border-white/5 bg-gray-950">
          <svg
            key={cycle}
            viewBox="0 0 240 150"
            className="absolute inset-0 h-full w-full"
          >
            {/* faint "drawing" underneath */}
            {PLAN_WALLS.map(([x1, y1, x2, y2], i) => (
              <line
                key={`g${i}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#374151"
                strokeWidth="5"
              />
            ))}
            <rect
              x="10"
              y="10"
              width="85"
              height="130"
              fill="#f97316"
              style={{ animation: "tw-fill 2.4s ease-out forwards" }}
            />
            <rect
              x="160"
              y="75"
              width="70"
              height="65"
              fill="#38bdf8"
              style={{ animation: "tw-fill 3s ease-out forwards" }}
            />
            {PLAN_WALLS.map(([x1, y1, x2, y2, d], i) => {
              const len = Math.hypot(x2 - x1, y2 - y1);
              return (
                <line
                  key={i}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={i < 4 ? "#f97316" : "#a78bfa"}
                  strokeWidth="3"
                  strokeLinecap="round"
                  className="tw-line"
                  style={{ "--len": len, animationDelay: `${d}s` }}
                />
              );
            })}
            {PLAN_DOORS.map(([x, y, d], i) => (
              <circle
                key={`d${i}`}
                cx={x}
                cy={y}
                r="5"
                fill="#e879f9"
                style={{
                  transformOrigin: `${x}px ${y}px`,
                  animation: `tw-pop .6s ease-out ${d}s both`,
                }}
              />
            ))}
          </svg>
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-orange-400/0 via-orange-400/40 to-orange-400/0"
            style={{ animation: "tw-scan 2.2s linear infinite" }}
          />
        </div>
        <div className="mb-1 flex items-baseline justify-between text-xs">
          <span className="font-semibold text-white">Tracing…</span>
          <span className="text-gray-400">
            {running.done}/{running.total} sheets
          </span>
        </div>
        <div className="mb-3 h-2 overflow-hidden rounded-full bg-gray-800">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.max(4, pct)}%`,
              backgroundImage:
                "repeating-linear-gradient(45deg,#f97316 0 7px,#fb923c 7px 14px)",
              backgroundSize: "28px 100%",
              animation: "tw-stripes .8s linear infinite",
            }}
          />
        </div>
        <ul className="max-h-32 space-y-1 overflow-y-auto text-[11px]">
          {(log || []).map((m, i, arr) => (
            <li key={i} className="flex items-center gap-2">
              {i < arr.length - 1 ? (
                <span className="text-green-400">✓</span>
              ) : (
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
              )}
              <span
                className={
                  i < arr.length - 1 ? "text-gray-500" : "text-gray-200"
                }
              >
                {m}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
// every assembly has a short NAME (what the estimator calls it) and a DESCRIPTION (everything in it, one
// item per line). Older / hand-added types without one get it from their layers.
export const descOf = (a) =>
  String(a?.description || "").trim() ||
  (a?.layers || []).filter(Boolean).join("\n");

export const suggestSheetKind = (label, text) => {
  const t = `${label || ""} ${text || ""}`.toUpperCase();
  if (/\bDEMO(LITION)?\b/.test(label || "")) return "other";
  if (/REFLECTED CEILING|\bRCP\b|CEILING PLAN/.test(t)) return "rcp";
  if (
    /ASSEMBL(Y|IES) SCHEDULE|WALL TYPES|PARTITION TYPES|CEILING TYPES|TYPICAL (WALL|PARTITION)|WALL ASSEMBL|ASSEMBLIES/.test(
      t,
    )
  )
    return "assembly";
  if (
    /FLOOR PLAN|BASEMENT PLAN|LEVEL \d+ PLAN|GROUND FLOOR|SECOND FLOOR|THIRD FLOOR|CELLAR PLAN|MEZZANINE PLAN/.test(
      t,
    )
  )
    return "floor";
  return "other";
};

// "BASEMENT RCP" ↔ "BASEMENT FLOOR PLAN"
const levelKey = (s) => {
  const t = String(s || "").toUpperCase();
  if (/BASEMENT|CELLAR|LOWER LEVEL|\bP1\b/.test(t)) return "B";
  if (/GROUND|FIRST|LEVEL ?1\b|MAIN FLOOR/.test(t)) return "1";
  const m = t.match(/(SECOND|THIRD|FOURTH|FIFTH)|LEVEL ?(\d+)/);
  if (m)
    return m[2] || { SECOND: "2", THIRD: "3", FOURTH: "4", FIFTH: "5" }[m[1]];
  return null;
};

const ROLES = [
  {
    key: "legend",
    label: "Legend",
    tip: "Assembly / wall-type legend — read for the types",
    on: "bg-violet-600 text-white",
  },
  {
    key: "floor",
    label: "Floor",
    tip: "Floor plan — walls are traced",
    on: "bg-orange-600 text-white",
  },
  {
    key: "rcp",
    label: "RCP",
    tip: "Reflected ceiling plan — ceilings are traced",
    on: "bg-sky-600 text-white",
  },
  { key: "skip", label: "Skip", tip: "Not used", on: "bg-gray-600 text-white" },
];
const TAG_SYMBOLS = [
  ["auto", "Auto-detect"],
  ["diamond", "◇ Diamond"],
  ["hexagon", "⬡ Hexagon"],
  ["circle", "○ Circle"],
  ["square", "□ Box"],
  ["oval", "⬭ Oval"],
  ["triangle", "△ Triangle"],
  ["none", "Plain text"],
  ["any", "Any label"],
];
// the wizard's older per-step choices → one role per sheet
const legacyRoles = (S) => {
  const r = {};
  for (const n of S.assemblyPages || []) r[n] = "legend";
  for (const n of Object.keys(S.floor || {})) r[n] = "floor";
  for (const n of Object.keys(S.rcp || {})) r[n] = "rcp";
  return r;
};

// ── small form controls for the options step (custom-drawn so the app's global form styles can't bloat them) ──
const fs12 = { fontSize: 12, lineHeight: "16px" };
function Toggle({ checked, onChange, title, desc, disabled, children }) {
  return (
    <div
      className={`flex items-start gap-4 py-3 ${disabled ? "opacity-50" : ""}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium text-gray-100">{title}</div>
        {desc && (
          <p className="mt-0.5 text-xs leading-relaxed text-gray-400">{desc}</p>
        )}
        {children}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 ${checked ? "bg-orange-600" : "bg-gray-700"}`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`}
        />
      </button>
    </div>
  );
}
function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-700 bg-gray-950/60 p-0.5">
      {options.map((o) => (
        <button
          type="button"
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${value === o.value ? "bg-orange-600 text-white shadow" : "text-gray-400 hover:text-gray-200"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
function ChoiceCard({ active, onClick, title, desc, tag }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${active ? "border-orange-500/70 bg-orange-500/10" : "border-gray-800 bg-gray-900/40 hover:border-gray-700 hover:bg-gray-800/40"}`}
    >
      <span
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${active ? "border-orange-500" : "border-gray-600"}`}
      >
        {active && <span className="h-2 w-2 rounded-full bg-orange-500" />}
      </span>
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2 text-[13px] font-medium text-gray-100">
          {title}
          {tag && (
            <span className="rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-300">
              {tag}
            </span>
          )}
        </span>
        {desc && (
          <span className="mt-0.5 block text-xs leading-relaxed text-gray-400">
            {desc}
          </span>
        )}
      </span>
    </button>
  );
}
function NiceSelect({
  value,
  onChange,
  children,
  className = "",
  small = false,
}) {
  return (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={fs12}
        className={`${small ? "h-7 pl-2 pr-7" : "h-9 pl-3 pr-8"} w-full appearance-none truncate rounded-lg border border-gray-700 bg-gray-950/60 text-gray-100 focus:border-orange-500 focus:outline-none`}
      >
        {children}
      </select>
      <svg
        className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  );
}
function NiceInput({ label, value, onChange, placeholder, bad, hint }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-gray-400">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={fs12}
        className={`h-9 w-full rounded-lg border bg-gray-950/60 px-3 text-gray-100 placeholder:text-gray-600 focus:outline-none ${bad ? "border-red-600 focus:border-red-500" : "border-gray-700 focus:border-orange-500"}`}
      />
      {hint && <span className="text-[11px] text-gray-500">{hint}</span>}
    </label>
  );
}
function OptGroup({ title, desc, right, children }) {
  return (
    <section className="rounded-xl border border-gray-800 bg-gray-900/60">
      <header className="flex items-start gap-3 border-b border-gray-800 px-4 py-3">
        <div className="min-w-0 flex-1">
          <h4 className="text-[13px] font-semibold text-white">{title}</h4>
          {desc && <p className="mt-0.5 text-xs text-gray-400">{desc}</p>}
        </div>
        {right}
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}
function Check({ checked, onChange, disabled, title }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={!!checked}
      disabled={disabled}
      title={title}
      onClick={() => onChange(!checked)}
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${checked ? "border-orange-500 bg-orange-600" : "border-gray-600 bg-gray-950/60 hover:border-gray-400"}`}
    >
      {checked && (
        <svg
          viewBox="0 0 16 16"
          className="h-3 w-3 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="M3.5 8.5l3 3 6-7" />
        </svg>
      )}
    </button>
  );
}
function Pill({ on, onClick, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[11px] font-medium transition-colors ${on ? "border-orange-500/60 bg-orange-500/15 text-orange-200" : "border-gray-700 text-gray-500 hover:text-gray-300"}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${on ? "bg-orange-400" : "bg-gray-600"}`}
      />
      {children}
    </button>
  );
}
function StatTile({ active, onClick, label, value, dot }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-w-0 flex-1 flex-col rounded-xl border px-3 py-2.5 text-left transition-colors ${active ? "border-orange-500/70 bg-orange-500/10" : "border-gray-800 bg-gray-900/60 hover:border-gray-700"}`}
    >
      <span className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400">
        {dot && <span className={`h-2 w-2 rounded-full ${dot}`} />}
        {label}
      </span>
      <span className="mt-0.5 text-lg font-semibold tabular-nums text-white">
        {value}
      </span>
    </button>
  );
}
const BTN_PRIMARY =
  "inline-flex h-8 items-center gap-1.5 rounded-lg bg-orange-600 px-4 text-xs font-semibold text-white shadow hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-40";
const BTN_SECONDARY =
  "inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-700 bg-gray-900 px-3 text-xs font-medium text-gray-200 hover:border-gray-600 hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40";
const BTN_GHOST =
  "inline-flex h-8 items-center rounded-lg px-2.5 text-xs font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200 disabled:opacity-40";
// sticky footer bar shared by every step
function WizFooter({ left, children }) {
  return (
    <footer className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 border-t border-white/5 bg-gray-900 px-5 py-3">
      <div className="min-w-0 flex-1 text-gray-400">{left}</div>
      {children}
    </footer>
  );
}
function SearchBox({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <svg
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="9" cy="9" r="6" />
        <path d="M14 14l4 4" />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={fs12}
        className="h-8 w-56 rounded-lg border border-gray-700 bg-gray-950/60 pl-8 pr-3 text-gray-100 placeholder:text-gray-600 focus:border-orange-500 focus:outline-none"
      />
    </div>
  );
}
const typeLabel = (a) =>
  `${a.no ? `#${a.no} · ` : ""}${a.tag ? `${a.tag} — ` : ""}${a.name}`;

export default function AssemblyWizard({
  open,
  onClose,
  pageCount,
  pageLabel,
  readSheetText,
  readAssemblies,
  scaleStatus,
  assemblies,
  setAssemblies,
  settings,
  setSettings,
  onRun,
  onGoToPage,
  parseFeetInput,
  fmtFeetIn,
  onSuggestRoles, // async (overwrite) → the viewer reads every title block and sets settings.sheetRoles
}) {
  const [step, setStep] = useState(1);
  const [optTab, setOptTab] = useState("walls"); // step 3 section
  const [typeFilter, setTypeFilter] = useState("all"); // step 2
  const [typeSearch, setTypeSearch] = useState("");
  const [reading, setReading] = useState("");
  const [readErr, setReadErr] = useState("");
  const [aiNote, setAiNote] = useState("");
  const [running, setRunning] = useState(null); // { done, total, msg }
  const [runLog, setRunLog] = useState([]); // the steps so far, for the tracing animation
  useEffect(() => {
    if (!running?.msg) return;
    setRunLog((prev) =>
      prev[prev.length - 1] === running.msg
        ? prev
        : [...prev, running.msg].slice(-30),
    );
  }, [running?.msg]);
  const [result, setResult] = useState(null);
  const [scales, setScales] = useState({}); // page → { ok, label, source }
  const [openRow, setOpenRow] = useState(null);

  const S = settings || {};
  const set = (patch) => setSettings((prev) => ({ ...(prev || {}), ...patch }));
  const pages = useMemo(
    () => Array.from({ length: pageCount }, (_, i) => i + 1),
    [pageCount],
  );

  // sheet roles: first open → carry over older choices, else read the title blocks and suggest
  const [suggesting, setSuggesting] = useState(false);
  const [sheetFilter, setSheetFilter] = useState("all");
  const [sheetSearch, setSheetSearch] = useState("");
  const suggest = async (overwrite) => {
    setSuggesting(true);
    try {
      if (onSuggestRoles) await onSuggestRoles(overwrite);
      else {
        // (no viewer helper: the sheet text, cheap)
        const roles = { ...(overwrite ? {} : S.sheetRoles || {}) };
        for (const n of pages) {
          if (roles[n]) continue;
          try {
            const k = suggestSheetKind(
              pageLabel(n),
              (await readSheetText(n, { quick: true })).slice(0, 4000),
            );
            roles[n] = k === "assembly" ? "legend" : k === "other" ? "skip" : k;
          } catch {
            /* unreadable */
          }
        }
        set({ sheetRoles: roles });
      }
    } finally {
      setSuggesting(false);
    }
  };
  useEffect(() => {
    if (!open || !pageCount || Object.keys(S.sheetRoles || {}).length) return;
    const old = legacyRoles(S);
    if (Object.keys(old).length) set({ sheetRoles: old });
    else suggest(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pageCount]);

  // the assembly sheet often states the wall height ("WALL HEIGHT 9'-0" …") — start from it
  useEffect(() => {
    if (!open || step !== 3 || S.defaultHeight) return;
    const stated = (assemblies || [])
      .filter((a) => a.kind === "wall" && a.trace)
      .map((a) => parseFeetInput(a.height || ""))
      .find((h) => h && h > 6 && h < 40);
    if (!stated) return;
    const txt = fmtFeetIn(stated),
      f = { ...(S.floor || {}) };
    for (const k of Object.keys(f))
      if (!f[k].height) f[k] = { ...f[k], height: txt };
    set({ defaultHeight: txt, floor: f });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  const roles = S.sheetRoles || {};
  const roleOf = (n) => roles[n] || roles[String(n)] || "";
  const rolePages = (r) => pages.filter((n) => roleOf(n) === r);
  useEffect(() => {
    if (!open || (step !== 1 && step !== 3)) return;
    let dead = false;
    (async () => {
      const want = pages.filter((n) => ["floor", "rcp"].includes(roleOf(n)));
      const out = {};
      for (const n of want) {
        try {
          out[n] = await scaleStatus(n);
        } catch {
          out[n] = { ok: false };
        }
      }
      if (!dead) setScales(out);
    })();
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, JSON.stringify(S.sheetRoles || {})]);

  if (!open) return null;

  const asmPages = rolePages("legend");
  // one role per sheet (click the active one again to clear it)
  const setRole = (n, r) => {
    const next = { ...roles };
    delete next[String(n)];
    if (r && roleOf(n) !== r) next[n] = r;
    else delete next[n];
    set({
      sheetRoles: next,
      assemblyPages: pages.filter((m) => next[m] === "legend"),
    });
  };

  async function doRead() {
    if (!asmPages.length)
      return setReadErr("Pick at least one sheet with the assemblies.");
    setReadErr("");
    setAiNote("");
    setReading("Reading the sheets…");
    try {
      const data = await readAssemblies(asmPages, (msg) => setReading(msg));
      const prev = new Map(
        (assemblies || []).map((a) => [`${a.kind}|${a.tag}|${a.name}`, a]),
      );
      const next = (data.assemblies || []).map((a) => {
        const old = prev.get(`${a.kind}|${a.tag}|${a.name}`);
        return {
          id: old?.id || uid(),
          // a type read again keeps its number (new ones get the next free number from the viewer)
          ...(old?.no ? { no: old.no } : {}),
          trace: old?.trace ?? (a.kind === "wall" || a.kind === "ceiling"),
          conditionId: old?.conditionId || null,
          ...a,
        };
      });
      // keep types the estimator added by hand
      const manual = (assemblies || []).filter((a) => a.source === "manual");
      setAssemblies([...next, ...manual]);
      if (data.aiSkipped)
        setAiNote(
          `AI reading skipped (${data.aiSkipped}) — showing what the text layer gives.`,
        );
      setStep(2);
    } catch (e) {
      setReadErr(e.message || "Could not read the sheets.");
    } finally {
      setReading("");
    }
  }

  const upd = (id, patch) =>
    setAssemblies((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    );
  const wallTypes = (assemblies || []).filter((a) => a.kind === "wall");
  const ceilTypes = (assemblies || []).filter((a) => a.kind === "ceiling");
  const selWalls = wallTypes.filter((a) => a.trace);
  const selCeils = ceilTypes.filter((a) => a.trace);

  // the sheets marked Floor / RCP, with their own options (height, walls / areas, rooms from)
  const floorPages = rolePages("floor").map((n) => ({
    n,
    on: true,
    height: S.floor?.[n]?.height ?? "",
    walls: S.floor?.[n]?.walls ?? true,
    areas: !!S.floor?.[n]?.areas,
  }));
  const rcpPages = rolePages("rcp").map((n) => ({
    n,
    on: true,
    from: S.rcp?.[n]?.from || "auto",
  }));
  const setFloor = (n, patch) =>
    set({
      floor: { ...(S.floor || {}), [n]: { ...(S.floor?.[n] || {}), ...patch } },
    });
  const setRcp = (n, patch) =>
    set({
      rcp: { ...(S.rcp || {}), [n]: { ...(S.rcp?.[n] || {}), ...patch } },
    });
  const heightOf = (p) => parseFeetInput(p.height || S.defaultHeight || "");
  const problems = [
    ...floorPages
      .filter((p) => p.on && (p.walls ?? true) && !heightOf(p))
      .map((p) => `${pageLabel(p.n)}: no wall height`),
    ...[...floorPages, ...rcpPages]
      .filter((p) => p.on && scales[p.n] && !scales[p.n].ok)
      .map((p) => `${pageLabel(p.n)}: no scale — calibrate it first`),
  ];
  const canRun =
    (floorPages.some((p) => p.on) || rcpPages.some((p) => p.on)) &&
    !problems.length &&
    (selWalls.length ||
      selCeils.length ||
      S.untaggedWall ||
      rcpPages.some((p) => p.on) ||
      floorPages.some((p) => p.areas));
  // which ceiling type each drawn grid becomes ("auto" = the type whose name/build-up says that grid)
  const GRID_ROWS = [
    ["2x4", "2'×4' grid"],
    ["2x2", "2'×2' grid"],
    ["none", "No grid (drywall)"],
  ];
  const autoGridType = (kind) => {
    const hits = ceilTypes.filter(
      (a) =>
        gridOfText([a.tag, a.name, ...(a.layers || [])].join(" ")) === kind,
    );
    return hits.find((a) => a.trace) || hits[0] || null;
  };
  const gridChoice = (kind) => (S.ceilGrid || {})[kind] ?? "auto";

  async function run() {
    setRunLog([]);
    setRunning({ done: 0, total: 1, msg: "Starting…" });
    try {
      const plan = {
        floor: floorPages
          .filter((p) => p.on)
          .map((p) => ({
            page: p.n,
            height: heightOf(p),
            walls: p.walls ?? true,
            areas: !!p.areas,
          })),
        // from: "self" = the RCP's own rooms · null = the matching floor plan, found by the viewer · n = that sheet
        rcp: rcpPages.map((p) => ({
          page: p.n,
          from:
            p.from === "self"
              ? "self"
              : p.from === "auto" || !p.from
                ? null
                : Number(p.from),
        })),
        wallTypes: selWalls,
        ceilingTypes: selCeils,
        untaggedWall: S.untaggedWall || "",
        areaTarget: S.areaTarget || "",
        ceilGrid: Object.fromEntries(
          GRID_ROWS.map(([k]) => {
            const v = gridChoice(k);
            return [k, v === "auto" ? autoGridType(k)?.id || "" : v];
          }),
        ),
        windowMode: S.windowMode || "deduct",
        windowHeight: parseFeetInput(S.windowHeight || "") || null,
        doorMode: S.doorMode || "deduct",
        // every door is deducted / counted at these sizes (not the drawn opening)
        doorHeight: parseFeetInput(S.doorHeight || "") || 7,
        doorWidth: parseFeetInput(S.doorWidth || "") || 38 / 12,
        doubleDoorWidth: parseFeetInput(S.doubleDoorWidth || "") || 74 / 12,
        wallLines: S.wallLines || "centre",
        wetSeparate: S.wetSeparate ?? true,
        counts: S.counts ?? true,
        cornerBead: S.cornerBead ?? true,
        openFaces: S.openFaces ?? true,
        exterior: S.exterior ?? true,
        aiWalls: S.aiWalls ?? true,
        aiOpenings: S.aiOpenings ?? true,
      };
      const res = await onRun(plan, (p) => setRunning(p));
      setResult(res);
      setStep(4);
    } catch (e) {
      setResult({ error: e.message || String(e) });
      setStep(4);
    } finally {
      setRunning(null);
    }
  }

  const STEPS = [
    [1, "Sheets"],
    [2, "Types to trace"],
    [3, "Trace options"],
    [4, "Result"],
  ];
  const stepper = (
    <ol className="flex min-w-0 items-center gap-1">
      {STEPS.map(([n, label], i) => {
        const cur = step === n;
        const done = n < step || (n === 4 && !!result && step !== 4);
        const off = n === 4 && !result;
        return (
          <li key={n} className="flex items-center gap-1">
            {i > 0 && (
              <span
                className={`mx-1 hidden h-px w-6 sm:block ${n <= step ? "bg-orange-500/60" : "bg-gray-700"}`}
              />
            )}
            <button
              type="button"
              onClick={() => setStep(n)}
              disabled={off}
              className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${cur ? "text-white" : "text-gray-400 hover:text-gray-200"}`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ring-1 ${cur ? "bg-orange-600 text-white ring-orange-500" : done ? "bg-orange-500/15 text-orange-300 ring-orange-500/40" : "bg-gray-800 text-gray-400 ring-gray-700"}`}
              >
                {done && !cur ? (
                  <svg
                    viewBox="0 0 16 16"
                    className="h-3 w-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path d="M3.5 8.5l3 3 6-7" />
                  </svg>
                ) : (
                  n
                )}
              </span>
              <span className="hidden md:inline">{label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
  const kindBadge = (k) => (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${KIND_STYLE[k] || KIND_STYLE.other}`}
    >
      {k}
    </span>
  );

  return (
    <div className="absolute inset-0 z-[1200] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm">
      <div className="relative flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl">
        {running && <TraceAnimation running={running} log={runLog} />}
        <div className="flex items-center gap-4 border-b border-white/5 px-5 py-3">
          <div className="shrink-0">
            <h2 className="text-sm font-semibold text-white">
              Assemblies &amp; trace
            </h2>
            <p className="text-[11px] text-gray-500">Step {step} of 4</p>
          </div>
          <div className="flex min-w-0 flex-1 justify-center">{stepper}</div>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white"
          >
            <svg
              viewBox="0 0 20 20"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto text-xs text-gray-300">
          {/* ───────────── 1. sheets ───────────── */}
          {step === 1 &&
            (() => {
              const counts = { legend: 0, floor: 0, rcp: 0, skip: 0, none: 0 };
              for (const n of pages) counts[roleOf(n) || "none"]++;
              const q = sheetSearch.trim().toLowerCase();
              const shown = pages.filter(
                (n) =>
                  (sheetFilter === "all" ||
                    (sheetFilter === "none"
                      ? !roleOf(n)
                      : roleOf(n) === sheetFilter)) &&
                  (!q ||
                    `${n} ${pageLabel(n)} ${S.sheetTitles?.[n]?.title || ""}`
                      .toLowerCase()
                      .includes(q)),
              );
              const tile = (key, label, dot) => (
                <StatTile
                  key={key}
                  active={sheetFilter === key}
                  onClick={() =>
                    setSheetFilter(sheetFilter === key ? "all" : key)
                  }
                  label={label}
                  value={key === "all" ? pages.length : counts[key]}
                  dot={dot}
                />
              );
              return (
                <div className="flex min-h-[540px] flex-col">
                  <div className="flex-1 space-y-4 p-5">
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-white">
                          What is each sheet?
                        </h3>
                        <p className="mt-0.5 max-w-2xl text-xs text-gray-400">
                          <span className="text-violet-300">Legend</span> sheets
                          are read for the wall and ceiling types. The trace
                          runs on exactly the{" "}
                          <span className="text-orange-300">Floor</span> and{" "}
                          <span className="text-sky-300">RCP</span> sheets
                          marked here — nothing else.
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => suggest(false)}
                          disabled={suggesting}
                          className={BTN_SECONDARY}
                          title="Read every title block and fill in the sheets not marked yet"
                        >
                          <svg
                            viewBox="0 0 20 20"
                            className="h-3.5 w-3.5 text-orange-400"
                            fill="currentColor"
                          >
                            <path d="M10 2l1.8 4.6L16.5 8l-4.7 1.4L10 14l-1.8-4.6L3.5 8l4.7-1.4z" />
                          </svg>
                          {suggesting
                            ? "Reading title blocks…"
                            : "Suggest from titles"}
                        </button>
                        <button
                          type="button"
                          onClick={() => suggest(true)}
                          disabled={suggesting}
                          className={BTN_GHOST}
                        >
                          Redo all
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            set({ sheetRoles: {}, assemblyPages: [] })
                          }
                          className={BTN_GHOST}
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                      {tile("all", "All sheets")}
                      {tile("legend", "Legend", "bg-violet-500")}
                      {tile("floor", "Floor plans", "bg-orange-500")}
                      {tile("rcp", "Ceiling plans", "bg-sky-500")}
                      {tile("none", "Not marked", "bg-gray-500")}
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <SearchBox
                        value={sheetSearch}
                        onChange={setSheetSearch}
                        placeholder="Find a sheet…"
                      />
                      {counts.floor > 0 && (
                        <label className="ml-auto flex items-center gap-2 text-xs text-gray-400">
                          Typical wall height
                          <input
                            value={S.defaultHeight || ""}
                            onChange={(e) =>
                              set({ defaultHeight: e.target.value })
                            }
                            placeholder={`9'-0"`}
                            style={fs12}
                            className={`h-8 w-24 rounded-lg border bg-gray-950/60 px-2.5 text-gray-100 focus:outline-none ${S.defaultHeight && !parseFeetInput(S.defaultHeight) ? "border-red-600" : "border-gray-700 focus:border-orange-500"}`}
                          />
                        </label>
                      )}
                    </div>

                    <div className="overflow-hidden rounded-xl border border-gray-800">
                      <div className="grid grid-cols-[40px_1fr_auto] items-center gap-3 border-b border-gray-800 bg-gray-950/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        <span className="text-right">#</span>
                        <span>Sheet</span>
                        <span>Use as</span>
                      </div>
                      <div className="max-h-[46vh] divide-y divide-gray-800/80 overflow-y-auto">
                        {shown.map((n) => {
                          const r = roleOf(n);
                          const sc = scales[n];
                          const t = S.sheetTitles?.[n];
                          const label = pageLabel(n);
                          return (
                            <div
                              key={n}
                              className={`grid grid-cols-[40px_1fr_auto] items-center gap-3 px-3 py-2 transition-colors hover:bg-gray-800/30 ${r === "skip" ? "opacity-50" : ""}`}
                            >
                              <span className="text-right font-mono text-[11px] text-gray-600">
                                {n}
                              </span>
                              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
                                <button
                                  type="button"
                                  onClick={() => onGoToPage(n)}
                                  className="min-w-0 max-w-full truncate text-left hover:text-orange-300"
                                  title={`${label}${t?.title && !label.includes(t.title) ? ` — ${t.title}` : ""} (open the sheet)`}
                                >
                                  <span className="text-[13px] text-gray-100">
                                    {label}
                                  </span>
                                  {t?.title && !label.includes(t.title) && (
                                    <span className="ml-2 text-[11px] text-gray-500">
                                      {t.title}
                                    </span>
                                  )}
                                </button>
                                {r === "floor" && (
                                  <span className="flex items-center gap-1.5">
                                    <input
                                      value={S.floor?.[n]?.height ?? ""}
                                      onChange={(e) =>
                                        setFloor(n, { height: e.target.value })
                                      }
                                      placeholder={S.defaultHeight || "Height"}
                                      title="Wall height on this floor"
                                      style={fs12}
                                      className={`h-7 w-20 rounded-md border bg-gray-950/60 px-2 text-gray-100 placeholder:text-gray-500 focus:outline-none ${!heightOf({ height: S.floor?.[n]?.height }) ? "border-red-700" : "border-gray-700 focus:border-orange-500"}`}
                                    />
                                    <Pill
                                      on={S.floor?.[n]?.walls ?? true}
                                      onClick={() =>
                                        setFloor(n, {
                                          walls: !(S.floor?.[n]?.walls ?? true),
                                        })
                                      }
                                      title="Trace the walls on this sheet"
                                    >
                                      Walls
                                    </Pill>
                                    <Pill
                                      on={!!S.floor?.[n]?.areas}
                                      onClick={() =>
                                        setFloor(n, {
                                          areas: !S.floor?.[n]?.areas,
                                        })
                                      }
                                      title="Also draw each room's floor area"
                                    >
                                      Room areas
                                    </Pill>
                                  </span>
                                )}
                                {r === "rcp" && (
                                  <NiceSelect
                                    small
                                    className="w-56"
                                    value={S.rcp?.[n]?.from || "auto"}
                                    onChange={(v) => setRcp(n, { from: v })}
                                  >
                                    <option value="auto">
                                      Rooms from matching floor plan
                                    </option>
                                    <option value="self">
                                      Rooms from this RCP
                                    </option>
                                    {rolePages("floor").map((m) => (
                                      <option key={m} value={String(m)}>
                                        Rooms from {pageLabel(m)}
                                      </option>
                                    ))}
                                  </NiceSelect>
                                )}
                                {(r === "floor" || r === "rcp") && sc && (
                                  <span
                                    className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium ${sc.ok ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}
                                  >
                                    {sc.ok
                                      ? sc.label || "Scale set"
                                      : "No scale"}
                                  </span>
                                )}
                              </div>
                              <div className="inline-flex rounded-lg border border-gray-700 bg-gray-950/60 p-0.5">
                                {ROLES.map((x) => (
                                  <button
                                    type="button"
                                    key={x.key}
                                    onClick={() => setRole(n, x.key)}
                                    title={x.tip}
                                    className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${r === x.key ? x.on : "text-gray-500 hover:text-gray-200"}`}
                                  >
                                    {x.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        {!shown.length && (
                          <div className="p-8 text-center text-gray-500">
                            No sheets match.
                          </div>
                        )}
                      </div>
                    </div>
                    {readErr && (
                      <div className="rounded-lg border border-red-800/60 bg-red-950/40 px-3 py-2 text-red-300">
                        {readErr}
                      </div>
                    )}
                  </div>
                  <WizFooter
                    left={
                      <>
                        <span className="text-gray-200">{counts.legend}</span>{" "}
                        legend ·{" "}
                        <span className="text-gray-200">{counts.floor}</span>{" "}
                        floor ·{" "}
                        <span className="text-gray-200">{counts.rcp}</span> RCP
                        {counts.none
                          ? ` · ${counts.none} not marked (left out)`
                          : ""}
                      </>
                    }
                  >
                    {(assemblies || []).length > 0 && (
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        className={BTN_SECONDARY}
                      >
                        Use the {assemblies.length} types already read
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={doRead}
                      disabled={!!reading || !asmPages.length}
                      className={BTN_PRIMARY}
                      title={
                        asmPages.length ? "" : "Mark at least one Legend sheet"
                      }
                    >
                      {reading ||
                        `Read ${asmPages.length || ""} legend sheet${asmPages.length === 1 ? "" : "s"}`}
                      {!reading && <span aria-hidden>→</span>}
                    </button>
                  </WizFooter>
                </div>
              );
            })()}
          {/* ───────────── 2. types ───────────── */}
          {step === 2 &&
            (() => {
              const all = assemblies || [];
              const tq = typeSearch.trim().toLowerCase();
              const shownTypes = all.filter(
                (a) =>
                  (typeFilter === "all" ||
                    (typeFilter === "other"
                      ? a.kind !== "wall" && a.kind !== "ceiling"
                      : a.kind === typeFilter)) &&
                  (!tq ||
                    `${a.no || ""} ${a.tag || ""} ${a.name || ""} ${descOf(a)}`
                      .toLowerCase()
                      .includes(tq)),
              );
              const traceable = shownTypes.filter(
                (a) => a.kind === "wall" || a.kind === "ceiling",
              );
              const allOn =
                traceable.length > 0 && traceable.every((a) => a.trace);
              const setAll = (v) =>
                setAssemblies((prev) =>
                  prev.map((a) =>
                    traceable.some((x) => x.id === a.id)
                      ? { ...a, trace: v }
                      : a,
                  ),
                );
              const kc = {
                all: all.length,
                wall: all.filter((a) => a.kind === "wall").length,
                ceiling: all.filter((a) => a.kind === "ceiling").length,
                other: all.filter(
                  (a) => a.kind !== "wall" && a.kind !== "ceiling",
                ).length,
              };
              const addType = (kind) =>
                setAssemblies((prev) => [
                  ...(prev || []),
                  {
                    id: uid(),
                    kind,
                    tag: "",
                    name:
                      kind === "wall" ? "New wall type" : "New ceiling type",
                    description: "",
                    layers: [],
                    trace: true,
                    verified: true,
                    source: "manual",
                  },
                ]);
              return (
                <div className="flex min-h-[540px] flex-col">
                  <div className="flex-1 space-y-4 p-5">
                    {aiNote && (
                      <div className="rounded-lg border border-yellow-700/40 bg-yellow-900/20 px-3 py-2 text-yellow-200">
                        {aiNote}
                      </div>
                    )}
                    <div className="flex flex-wrap items-start gap-4">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-white">
                          Pick the types to trace
                        </h3>
                        <p className="mt-0.5 max-w-xl text-xs text-gray-400">
                          Each type's <span className="text-gray-200">tag</span>{" "}
                          must match the mark on the plans (MS.12, W3, C1…) —
                          fix it here if the reading is off. Rows marked{" "}
                          <span className="rounded bg-yellow-500/15 px-1 text-yellow-300">
                            check
                          </span>{" "}
                          were read from a picture of the sheet.
                        </p>
                      </div>
                      <div className="w-60 rounded-xl border border-gray-800 bg-gray-900/60 p-3">
                        <div className="mb-1.5 text-[11px] font-medium text-gray-400">
                          Wall tags are drawn in
                        </div>
                        <NiceSelect
                          value={S.tagSymbol || "auto"}
                          onChange={(v) => set({ tagSymbol: v })}
                        >
                          {TAG_SYMBOLS.map(([k, l]) => (
                            <option key={k} value={k}>
                              {l}
                            </option>
                          ))}
                        </NiceSelect>
                        <p className="mt-1.5 text-[11px] leading-snug text-gray-500">
                          Door tags, room numbers and keynotes are ignored.
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <Segmented
                        value={typeFilter}
                        onChange={setTypeFilter}
                        options={[
                          { value: "all", label: `All ${kc.all}` },
                          { value: "wall", label: `Walls ${kc.wall}` },
                          { value: "ceiling", label: `Ceilings ${kc.ceiling}` },
                          ...(kc.other
                            ? [{ value: "other", label: `Other ${kc.other}` }]
                            : []),
                        ]}
                      />
                      <SearchBox
                        value={typeSearch}
                        onChange={setTypeSearch}
                        placeholder="Search tag, name, build-up…"
                      />
                    </div>

                    <div className="overflow-hidden rounded-xl border border-gray-800">
                      <table className="w-full text-left">
                        <thead className="border-b border-gray-800 bg-gray-950/40 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                          <tr>
                            <th className="w-10 px-3 py-2">
                              <Check
                                checked={allOn}
                                onChange={setAll}
                                disabled={!traceable.length}
                                title="Select all shown"
                              />
                            </th>
                            <th className="w-12 py-2">#</th>
                            <th className="w-28 py-2">Kind</th>
                            <th className="w-28 py-2">Tag</th>
                            <th className="py-2">Name &amp; build-up</th>
                            <th className="w-32 py-2">Rating</th>
                            <th className="w-14 py-2 pr-3 text-right">Sheet</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/80">
                          {shownTypes.map((a) => {
                            const d = descOf(a);
                            const nItems = d ? d.split("\n").length : 0;
                            const canTrace =
                              a.kind === "wall" || a.kind === "ceiling";
                            return (
                              <React.Fragment key={a.id}>
                                <tr
                                  className={`align-top transition-colors hover:bg-gray-800/30 ${a.trace ? "bg-orange-500/[0.04]" : ""}`}
                                >
                                  <td className="px-3 py-2.5">
                                    <Check
                                      checked={!!a.trace}
                                      disabled={!canTrace}
                                      onChange={(v) => upd(a.id, { trace: v })}
                                      title={
                                        canTrace
                                          ? "Trace this type"
                                          : "Only walls and ceilings are traced"
                                      }
                                    />
                                  </td>
                                  <td
                                    className="py-2.5 font-mono text-[11px] text-orange-300/90"
                                    title="The assembly's number — the same on its card and in the estimate"
                                  >
                                    {a.no ? `#${a.no}` : "…"}
                                  </td>
                                  <td className="py-2 pr-2">
                                    <NiceSelect
                                      small
                                      value={a.kind}
                                      onChange={(v) => upd(a.id, { kind: v })}
                                    >
                                      {[
                                        "wall",
                                        "ceiling",
                                        "floor",
                                        "roof",
                                        "other",
                                      ].map((k) => (
                                        <option key={k} value={k}>
                                          {k[0].toUpperCase() + k.slice(1)}
                                        </option>
                                      ))}
                                    </NiceSelect>
                                  </td>
                                  <td className="py-2 pr-2">
                                    <input
                                      value={a.tag || ""}
                                      onChange={(e) =>
                                        upd(a.id, {
                                          tag: e.target.value
                                            .toUpperCase()
                                            .replace(/\s+/g, ""),
                                        })
                                      }
                                      placeholder="Tag"
                                      style={fs12}
                                      className="h-7 w-full rounded-md border border-gray-700 bg-gray-950/60 px-2 font-mono text-gray-100 placeholder:text-gray-600 focus:border-orange-500 focus:outline-none"
                                    />
                                  </td>
                                  <td className="py-2 pr-3">
                                    <div className="flex items-center gap-2">
                                      <input
                                        value={a.name || ""}
                                        onChange={(e) =>
                                          upd(a.id, { name: e.target.value })
                                        }
                                        placeholder="Short name"
                                        maxLength={60}
                                        style={{ fontSize: 13 }}
                                        className="h-7 min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1.5 font-medium text-gray-100 hover:border-gray-700 focus:border-orange-500 focus:bg-gray-950/60 focus:outline-none"
                                      />
                                      {!a.verified && (
                                        <span
                                          className="rounded bg-yellow-500/15 px-1.5 py-0.5 text-[10px] font-medium text-yellow-300"
                                          title="Read from an image of the sheet — check the tag and build-up"
                                        >
                                          check
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setOpenRow(
                                            openRow === a.id ? null : a.id,
                                          )
                                        }
                                        className="inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-md px-1.5 text-[11px] text-gray-500 hover:bg-gray-800 hover:text-gray-200"
                                      >
                                        {nItems
                                          ? `${nItems} item${nItems === 1 ? "" : "s"}`
                                          : "Add items"}
                                        <svg
                                          viewBox="0 0 20 20"
                                          className={`h-3 w-3 transition-transform ${openRow === a.id ? "rotate-180" : ""}`}
                                          fill="currentColor"
                                        >
                                          <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" />
                                        </svg>
                                      </button>
                                    </div>
                                    {openRow !== a.id && (
                                      <button
                                        type="button"
                                        onClick={() => setOpenRow(a.id)}
                                        title={
                                          d ||
                                          "No items read — click to add them"
                                        }
                                        className={`mt-0.5 line-clamp-2 w-full px-1.5 text-left text-[11px] leading-snug ${d ? "text-gray-500 hover:text-gray-300" : "italic text-amber-400/80"}`}
                                      >
                                        {d
                                          ? d.split("\n").join(" · ")
                                          : "No build-up — click to add what's in this assembly"}
                                      </button>
                                    )}
                                  </td>
                                  <td className="py-2.5 pr-2">
                                    <div className="flex flex-wrap gap-1">
                                      {[
                                        a.fireRating,
                                        a.stc && `STC ${a.stc}`,
                                        a.ul,
                                      ]
                                        .filter(Boolean)
                                        .map((r) => (
                                          <span
                                            key={r}
                                            className="rounded bg-red-500/10 px-1.5 py-0.5 text-[10px] font-medium text-red-300"
                                          >
                                            {r}
                                          </span>
                                        ))}
                                    </div>
                                  </td>
                                  <td className="py-2.5 pr-3 text-right text-gray-500">
                                    {a.page ? (
                                      <button
                                        type="button"
                                        onClick={() => onGoToPage(a.page)}
                                        className="underline decoration-dotted hover:text-gray-200"
                                      >
                                        {a.page}
                                      </button>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                </tr>
                                {openRow === a.id && (
                                  <tr className="bg-gray-950/50">
                                    <td />
                                    <td colSpan={6} className="py-3 pr-3">
                                      <div className="mb-1.5 text-[11px] font-medium text-gray-400">
                                        Build-up — every item in this assembly,
                                        one per line
                                      </div>
                                      <textarea
                                        value={a.description ?? d}
                                        onChange={(e) =>
                                          upd(a.id, {
                                            description: e.target.value,
                                            layers: e.target.value
                                              .split("\n")
                                              .map((x) => x.trim())
                                              .filter(Boolean),
                                          })
                                        }
                                        rows={Math.min(
                                          12,
                                          Math.max(3, nItems + 1),
                                        )}
                                        style={{ fontSize: 11 }}
                                        className="w-full rounded-lg border border-gray-700 bg-gray-950/60 p-2 font-mono text-gray-300 focus:border-orange-500 focus:outline-none"
                                        placeholder={`1/2" GYPSUM BOARD\n3-5/8" STEEL STUDS @ 16" O.C.\n1/2" GYPSUM BOARD`}
                                      />
                                      <div className="mt-2 flex flex-wrap items-center gap-2">
                                        {kindBadge(a.kind)}
                                        <span className="text-[11px] text-gray-500">
                                          {{
                                            "ai-image":
                                              "Read from the sheet image only — check",
                                            manual: "Added by hand",
                                            legend:
                                              "Found on the legend (text layer)",
                                            "legend+ai":
                                              "Found on the legend · build-up read by AI from the full-resolution panel",
                                          }[a.source] ||
                                            "Read from the sheet text"}
                                          {a.baseTag &&
                                            ` · based on ${a.baseTag}`}
                                          {a.height &&
                                            ` · height on sheet: ${a.height}`}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setAssemblies((prev) =>
                                              prev.filter((x) => x.id !== a.id),
                                            )
                                          }
                                          className="ml-auto rounded-md px-2 py-1 text-[11px] font-medium text-red-400 hover:bg-red-500/10"
                                        >
                                          Remove type
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                          {!shownTypes.length && (
                            <tr>
                              <td
                                colSpan={7}
                                className="p-8 text-center text-gray-500"
                              >
                                {all.length
                                  ? "No types match."
                                  : "No types read yet. Add them by hand, or mark the legend sheets in step 1."}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => addType("wall")}
                        className={BTN_SECONDARY}
                      >
                        + Wall type
                      </button>
                      <button
                        type="button"
                        onClick={() => addType("ceiling")}
                        className={BTN_SECONDARY}
                      >
                        + Ceiling type
                      </button>
                    </div>
                  </div>
                  <WizFooter
                    left={
                      <>
                        <span className="text-gray-200">{selWalls.length}</span>{" "}
                        wall ·{" "}
                        <span className="text-gray-200">{selCeils.length}</span>{" "}
                        ceiling types to trace
                      </>
                    }
                  >
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className={BTN_SECONDARY}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className={BTN_PRIMARY}
                    >
                      Next: trace options <span aria-hidden>→</span>
                    </button>
                  </WizFooter>
                </div>
              );
            })()}
          {/* ───────────── 3. trace options ───────────── */}
          {step === 3 &&
            (() => {
              const wallMode = !S.untaggedWall
                ? "only"
                : S.untaggedWall === "__guess"
                  ? "guess"
                  : S.untaggedWall === "__untagged"
                    ? "draw"
                    : "assign";
              const centre = (S.wallLines || "centre") === "centre";
              const doorDeduct = (S.doorMode || "deduct") === "deduct";
              const winDeduct = (S.windowMode || "deduct") === "deduct";
              const counts = S.counts ?? true;
              const MODE_LABEL = {
                only: "Tagged walls only",
                guess: "Tagged + best guess",
                draw: "All walls",
                assign: "Untagged → one type",
              };
              const TABS = [
                {
                  key: "walls",
                  label: "Walls",
                  sub: `${MODE_LABEL[wallMode]} · ${centre ? "centreline" : "room faces"}`,
                  off: !floorPages.length,
                },
                {
                  key: "openings",
                  label: "Doors & windows",
                  sub: counts
                    ? `Counted · doors ${doorDeduct ? "deducted" : "gapped"}`
                    : `Not counted · doors ${doorDeduct ? "deducted" : "gapped"}`,
                  off: !floorPages.length,
                },
                {
                  key: "ceilings",
                  label: "Ceilings & rooms",
                  sub: rcpPages.length
                    ? `${rcpPages.length} RCP${rcpPages.length === 1 ? "" : "s"}`
                    : "No RCPs marked",
                },
              ];
              const tab = optTab;
              const nSheets = floorPages.length + rcpPages.length;
              return (
                <div className="flex min-h-[540px] flex-col">
                  <div className="flex min-h-0 flex-1 flex-col md:flex-row">
                    {/* left rail: the sheets in this trace + the option sections */}
                    <aside className="shrink-0 space-y-4 border-b border-white/5 bg-gray-950/40 p-4 md:w-64 md:border-b-0 md:border-r">
                      <div>
                        <div className="mb-2 flex items-center">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                            Sheets in this trace
                          </span>
                          <button
                            type="button"
                            onClick={() => setStep(1)}
                            className="ml-auto text-[11px] font-medium text-orange-400 hover:text-orange-300"
                          >
                            Edit
                          </button>
                        </div>
                        <div className="space-y-1.5">
                          {floorPages.map((p) => (
                            <div
                              key={`f${p.n}`}
                              className="flex items-center gap-2 rounded-lg border border-gray-800 bg-gray-900/60 px-2.5 py-2"
                            >
                              <span className="rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-bold text-orange-300">
                                FLOOR
                              </span>
                              <span className="min-w-0 flex-1 truncate text-gray-200">
                                {pageLabel(p.n)}
                              </span>
                              <span
                                className={`text-[11px] ${heightOf(p) ? "text-gray-400" : "font-semibold text-red-400"}`}
                                title="Wall height"
                              >
                                {heightOf(p)
                                  ? fmtFeetIn(heightOf(p))
                                  : "no height"}
                              </span>
                              {scales[p.n] && !scales[p.n].ok && (
                                <span className="text-[11px] font-semibold text-red-400">
                                  no scale
                                </span>
                              )}
                            </div>
                          ))}
                          {rcpPages.map((p) => (
                            <div
                              key={`r${p.n}`}
                              className="flex items-center gap-2 rounded-lg border border-gray-800 bg-gray-900/60 px-2.5 py-2"
                            >
                              <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-bold text-sky-300">
                                RCP
                              </span>
                              <span className="min-w-0 flex-1 truncate text-gray-200">
                                {pageLabel(p.n)}
                              </span>
                              {scales[p.n] && !scales[p.n].ok && (
                                <span className="text-[11px] font-semibold text-red-400">
                                  no scale
                                </span>
                              )}
                            </div>
                          ))}
                          {!nSheets && (
                            <button
                              type="button"
                              onClick={() => setStep(1)}
                              className="w-full rounded-lg border border-dashed border-gray-700 px-3 py-3 text-left text-gray-400 hover:border-orange-500/60 hover:text-gray-200"
                            >
                              No sheets marked yet — mark floor plans or RCPs in
                              step 1.
                            </button>
                          )}
                        </div>
                      </div>
                      <nav className="space-y-1">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                          Options
                        </div>
                        {TABS.map((t) => (
                          <button
                            type="button"
                            key={t.key}
                            onClick={() => setOptTab(t.key)}
                            className={`flex w-full flex-col rounded-lg border-l-2 px-3 py-2 text-left transition-colors ${tab === t.key ? "border-orange-500 bg-gray-800/70" : "border-transparent hover:bg-gray-800/40"} ${t.off ? "opacity-50" : ""}`}
                          >
                            <span
                              className={`text-[13px] font-medium ${tab === t.key ? "text-white" : "text-gray-300"}`}
                            >
                              {t.label}
                            </span>
                            <span className="truncate text-[11px] text-gray-500">
                              {t.sub}
                            </span>
                          </button>
                        ))}
                      </nav>
                    </aside>

                    {/* the section being edited */}
                    <div className="min-w-0 flex-1 space-y-4 p-5">
                      {tab === "walls" && (
                        <>
                          {!floorPages.length && (
                            <div className="rounded-lg border border-yellow-700/40 bg-yellow-900/20 px-3 py-2 text-yellow-200">
                              No floor plans are marked, so no walls will be
                              traced.
                            </div>
                          )}
                          <OptGroup
                            title="Which walls get drawn"
                            desc={`Walls are typed by the tags on the plan. ${selWalls.length} wall type${selWalls.length === 1 ? "" : "s"} picked to trace.`}
                          >
                            <div className="grid gap-2 sm:grid-cols-2">
                              <ChoiceCard
                                active={wallMode === "only"}
                                onClick={() => set({ untaggedWall: "" })}
                                title="Tagged walls only"
                                tag="Recommended"
                                desc="Only lines a picked type claims (by tag, line colour or the AI check). Everything else is left out."
                              />
                              <ChoiceCard
                                active={wallMode === "guess"}
                                onClick={() => set({ untaggedWall: "__guess" })}
                                title="Tagged + best guess"
                                desc="Untagged lines take the type used for that wall thickness on the sheet. No match → left out."
                              />
                              <ChoiceCard
                                active={wallMode === "draw"}
                                onClick={() =>
                                  set({ untaggedWall: "__untagged" })
                                }
                                title="Draw every wall"
                                desc="Untagged lines are drawn as “Untagged wall” — click one later to assign it."
                              />
                              <ChoiceCard
                                active={wallMode === "assign"}
                                onClick={() =>
                                  wallTypes.length &&
                                  set({
                                    untaggedWall:
                                      wallMode === "assign"
                                        ? S.untaggedWall
                                        : (selWalls[0] || wallTypes[0]).id,
                                  })
                                }
                                title="Untagged → one type"
                                desc={
                                  wallTypes.length
                                    ? "Every untagged line goes to the type you choose."
                                    : "Read the assembly legend first (step 2)."
                                }
                              />
                            </div>
                            {wallMode === "assign" && (
                              <div className="mt-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                                <span className="shrink-0 text-xs text-gray-400">
                                  Untagged walls become
                                </span>
                                <NiceSelect
                                  className="min-w-0 flex-1"
                                  value={S.untaggedWall}
                                  onChange={(v) => set({ untaggedWall: v })}
                                >
                                  {wallTypes.map((a) => (
                                    <option key={a.id} value={a.id}>
                                      {typeLabel(a)}
                                    </option>
                                  ))}
                                </NiceSelect>
                              </div>
                            )}
                          </OptGroup>

                          <OptGroup
                            title="Line style"
                            desc="How a wall shared by two rooms is measured."
                          >
                            <div className="grid gap-2 sm:grid-cols-2">
                              <ChoiceCard
                                active={centre}
                                onClick={() => set({ wallLines: "centre" })}
                                title="Centreline"
                                tag="Recommended"
                                desc="One line per wall — a shared wall is counted once."
                              />
                              <ChoiceCard
                                active={!centre}
                                onClick={() => set({ wallLines: "faces" })}
                                title="Room faces"
                                desc="Each room's side is drawn — a shared wall is drawn twice."
                              />
                            </div>
                          </OptGroup>

                          <OptGroup title="Extras">
                            <div className="-my-1 divide-y divide-gray-800">
                              <Toggle
                                checked={S.openFaces ?? true}
                                onChange={(v) => set({ openFaces: v })}
                                title="Walls in open areas"
                                desc="Also draw tagged walls with no closed room beside them (open plan, lobbies)."
                              />
                              <Toggle
                                checked={S.exterior ?? true}
                                onChange={(v) => set({ exterior: v })}
                                title="Exterior wall as one continuous line"
                                desc="The outside wall is traced once through its whole thickness (brick, cavity, block, studs) instead of several lines inside it, typed by the exterior tags printed outside, with its windows deducted."
                              />
                              <Toggle
                                checked={S.wetSeparate ?? true}
                                onChange={(v) => set({ wetSeparate: v })}
                                title="Washroom walls as their own assembly"
                                desc="Same tag, separate line item for the moisture board — washroom, toilet, W/C, bath, shower."
                              />
                              <Toggle
                                checked={S.cornerBead ?? true}
                                onChange={(v) => set({ cornerBead: v })}
                                title="Corner bead"
                                desc="Counts clear outside corners and free wall ends on traced walls (corners × wall height = LF). Door jambs, windows and short jogs are skipped — add any missed ones by hand."
                              />
                              <Toggle
                                checked={S.aiWalls ?? true}
                                onChange={(v) => set({ aiWalls: v })}
                                title="AI double-check"
                                desc="Walls the tags don't settle are read by the AI from the tags and arrows around them — several walls per picture, and never the same one twice."
                              />
                            </div>
                          </OptGroup>
                        </>
                      )}

                      {tab === "openings" && (
                        <>
                          <OptGroup title="Counts">
                            <div className="-my-1 divide-y divide-gray-800">
                              <Toggle
                                checked={counts}
                                onChange={(v) => set({ counts: v })}
                                title="Count doors and windows"
                                desc="Places a marker on each one, booked to your count assemblies (HM frames etc.)."
                              />
                              <Toggle
                                checked={S.aiOpenings ?? true}
                                onChange={(v) => set({ aiOpenings: v })}
                                disabled={!counts}
                                title="AI double-check openings"
                                desc="Only where the trace is unsure — a door swing or D / W tag with no marker. Everything else stands as counted, so it costs little."
                              />
                            </div>
                          </OptGroup>

                          <OptGroup
                            title="Doors"
                            desc="Sizes used for counts and deductions — the drawing only decides single or double."
                            right={
                              <Segmented
                                value={doorDeduct ? "deduct" : "gap"}
                                onChange={(v) => set({ doorMode: v })}
                                options={[
                                  {
                                    value: "deduct",
                                    label: "Deduct from wall",
                                  },
                                  { value: "gap", label: "Leave a gap" },
                                ]}
                              />
                            }
                          >
                            <p className="mb-3 text-xs text-gray-400">
                              {doorDeduct
                                ? "The wall line runs through the door; width × door height is deducted from the wall SF."
                                : "The wall line stops at each door — no wall is measured across the opening."}
                            </p>
                            <div className="grid gap-3 sm:grid-cols-3">
                              {[
                                ["doorWidth", "Single door width", `3'-2"`],
                                [
                                  "doubleDoorWidth",
                                  "Double door width",
                                  `6'-2"`,
                                ],
                                ["doorHeight", "Door height", `7'-0"`],
                              ].map(([k, label, ph]) => (
                                <NiceInput
                                  key={k}
                                  label={label}
                                  value={S[k] ?? ""}
                                  onChange={(v) => set({ [k]: v })}
                                  placeholder={ph}
                                  bad={!!S[k] && !parseFeetInput(S[k])}
                                />
                              ))}
                            </div>
                            <p className="mt-2 text-[11px] text-gray-500">
                              Leave blank to use the size shown.
                            </p>
                          </OptGroup>

                          <OptGroup
                            title="Windows"
                            desc={
                              (S.windowTags ?? true)
                                ? "Only windows the plan tags (W1, W-2, CW4 …) are taken — untagged bands are treated as wall."
                                : "Every glazing band the trace finds is taken as a window, tagged or not."
                            }
                            right={
                              <Segmented
                                value={winDeduct ? "deduct" : "gap"}
                                onChange={(v) => set({ windowMode: v })}
                                options={[
                                  {
                                    value: "deduct",
                                    label: "Deduct from wall",
                                  },
                                  { value: "gap", label: "Leave a gap" },
                                ]}
                              />
                            }
                          >
                            <div className="-mt-1 mb-2 border-b border-gray-800">
                              <Toggle
                                checked={S.windowTags ?? true}
                                onChange={(v) => set({ windowTags: v })}
                                title="Only tagged windows"
                                desc="Recommended. Stops shelves, sills and hatching from being read as windows inside the takeoff."
                              />
                            </div>
                            {winDeduct ? (
                              <div className="grid gap-3 sm:grid-cols-[1fr_180px] sm:items-end">
                                <p className="text-xs text-gray-400">
                                  The wall line runs through the window; its
                                  length is taken off the net LF, and length ×
                                  the height here off the SF.
                                </p>
                                <NiceInput
                                  label="SF deduction height"
                                  value={S.windowHeight ?? ""}
                                  onChange={(v) => set({ windowHeight: v })}
                                  placeholder="Full wall height"
                                  bad={
                                    !!S.windowHeight &&
                                    !parseFeetInput(S.windowHeight)
                                  }
                                />
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400">
                                The wall line stops at each window, like doors.
                              </p>
                            )}
                          </OptGroup>
                        </>
                      )}

                      {tab === "ceilings" && (
                        <>
                          {!rcpPages.length ? (
                            <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-gray-700 bg-gray-900/40 p-5">
                              <div>
                                <h4 className="text-[13px] font-semibold text-white">
                                  No reflected ceiling plans in this trace
                                </h4>
                                <p className="mt-1 text-xs text-gray-400">
                                  Mark the RCP sheets in step 1 to trace a
                                  ceiling in every room.
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="rounded-lg border border-gray-700 px-3 py-1.5 text-xs font-medium text-gray-200 hover:border-orange-500/60 hover:text-white"
                              >
                                Mark RCP sheets
                              </button>
                            </div>
                          ) : (
                            <>
                              <OptGroup
                                title="Ceiling plans"
                                desc="Each room gets one ceiling for its whole area, so bulkheads and light coves never cut a room up."
                              >
                                <div className="divide-y divide-gray-800">
                                  {rcpPages.map((p) => (
                                    <div
                                      key={p.n}
                                      className="flex items-center gap-3 py-2"
                                    >
                                      <span className="min-w-0 flex-1 truncate text-gray-200">
                                        {pageLabel(p.n)}
                                      </span>
                                      <span className="text-[11px] text-gray-500">
                                        Rooms from{" "}
                                        <span className="text-gray-300">
                                          {p.from === "self"
                                            ? "this RCP"
                                            : p.from === "auto"
                                              ? "matching floor plan"
                                              : pageLabel(Number(p.from))}
                                        </span>
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </OptGroup>
                              <OptGroup
                                title="Rooms with no ceiling tag"
                                desc="Typed by the grid drawn in the room. “Auto” picks the type whose name or build-up matches that grid."
                              >
                                <div className="divide-y divide-gray-800">
                                  {GRID_ROWS.map(([k, label]) => {
                                    const auto = autoGridType(k);
                                    return (
                                      <div
                                        key={k}
                                        className="grid items-center gap-2 py-2 sm:grid-cols-[160px_1fr]"
                                      >
                                        <span className="text-[13px] text-gray-200">
                                          {label}
                                        </span>
                                        <NiceSelect
                                          value={gridChoice(k)}
                                          onChange={(v) =>
                                            set({
                                              ceilGrid: {
                                                ...(S.ceilGrid || {}),
                                                [k]: v,
                                              },
                                            })
                                          }
                                        >
                                          <option value="auto">
                                            {auto
                                              ? `Auto — ${typeLabel(auto)}`
                                              : "Auto — Untagged ceiling"}
                                          </option>
                                          <option value="">
                                            Untagged ceiling
                                          </option>
                                          {ceilTypes.map((a) => (
                                            <option key={a.id} value={a.id}>
                                              {typeLabel(a)}
                                            </option>
                                          ))}
                                        </NiceSelect>
                                      </div>
                                    );
                                  })}
                                </div>
                              </OptGroup>
                            </>
                          )}
                          <OptGroup
                            title="Room areas"
                            desc="Where room areas from floor plans set to “areas” are booked."
                          >
                            <NiceSelect
                              value={S.areaTarget || ""}
                              onChange={(v) => set({ areaTarget: v })}
                            >
                              <option value="">Floor area (by room)</option>
                              {ceilTypes.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {typeLabel(a)}
                                </option>
                              ))}
                            </NiceSelect>
                          </OptGroup>
                        </>
                      )}
                    </div>
                  </div>

                  {/* footer: what will run, what's blocking it */}
                  <footer className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 border-t border-white/5 bg-gray-900 px-5 py-3">
                    <div className="min-w-0 flex-1">
                      {running ? (
                        <span className="text-orange-300">
                          {running.msg} ({running.done}/{running.total})
                        </span>
                      ) : problems.length ? (
                        <span className="inline-flex items-center gap-2 rounded-lg bg-red-950/50 px-2.5 py-1 text-red-300">
                          <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                          {problems.slice(0, 2).join(" · ")}
                          {problems.length > 2
                            ? ` · +${problems.length - 2} more`
                            : ""}
                        </span>
                      ) : (
                        <span className="text-gray-400">
                          <span className="text-gray-200">
                            {floorPages.length} floor plan
                            {floorPages.length === 1 ? "" : "s"}
                          </span>
                          {" · "}
                          <span className="text-gray-200">
                            {rcpPages.length} RCP
                            {rcpPages.length === 1 ? "" : "s"}
                          </span>
                          {" · "}
                          {selWalls.length} wall / {selCeils.length} ceiling
                          type
                          {selWalls.length + selCeils.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className={BTN_SECONDARY}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={run}
                      disabled={!canRun || !!running}
                      className={BTN_PRIMARY}
                    >
                      {running
                        ? "Tracing…"
                        : `Trace ${nSheets} sheet${nSheets === 1 ? "" : "s"}`}
                    </button>
                  </footer>
                </div>
              );
            })()}

          {/* ───────────── 4. result ───────────── */}
          {step === 4 &&
            result &&
            (() => {
              if (result.error)
                return (
                  <div className="flex min-h-[420px] flex-col">
                    <div className="flex-1 p-5">
                      <div className="rounded-xl border border-red-800/60 bg-red-950/30 p-4">
                        <h3 className="text-[13px] font-semibold text-red-200">
                          The trace stopped
                        </h3>
                        <p className="mt-1 text-xs text-red-300">
                          {result.error}
                        </p>
                      </div>
                    </div>
                    <WizFooter>
                      <button
                        type="button"
                        onClick={() => setStep(3)}
                        className={BTN_SECONDARY}
                      >
                        Back to options
                      </button>
                      <button
                        type="button"
                        onClick={run}
                        className={BTN_PRIMARY}
                      >
                        Try again
                      </button>
                    </WizFooter>
                  </div>
                );
              const sumObj = (k) =>
                result.pages.reduce(
                  (t, r) =>
                    t +
                    Object.values(r[k] || {}).reduce((a, v) => a + (v || 0), 0),
                  0,
                );
              const totLf = sumObj("walls"),
                totSf = sumObj("ceilings");
              const cnt = (k) =>
                result.pages.reduce((t, r) => t + (r.counts?.[k] || 0), 0);
              const toCheck = result.pages.reduce(
                (t, r) =>
                  t +
                  (r.openings
                    ? (r.openings.flagged || 0) + (r.openings.toCheck || 0)
                    : 0),
                0,
              );
              const fmt0 = (v) => Math.round(v).toLocaleString();
              const notesOf = (r) => {
                const N = [];
                const add = (tone, text) => N.push({ tone, text });
                if (r.untaggedLf > 0.5)
                  add(
                    "warn",
                    `${fmt0(r.untaggedLf)} LF drawn as “Untagged wall” — click a line to assign it`,
                  );
                if (r.checkLf > 0.5)
                  add(
                    "warn",
                    `${fmt0(r.checkLf)} LF where tags disagree or sit far off (dashed) — click to approve or switch`,
                  );
                if (r.guessedLf > 0.5)
                  add(
                    "warn",
                    `${fmt0(r.guessedLf)} LF typed from wall thickness (dashed) — check`,
                  );
                if (r.untypedSf > 0.5)
                  add(
                    "warn",
                    `${fmt0(r.untypedSf)} SF drawn as “Untagged ceiling” — click an area to assign it`,
                  );
                if (r.openings && (r.openings.flagged || r.openings.toCheck))
                  add(
                    "warn",
                    `${(r.openings.flagged || 0) + (r.openings.toCheck || 0)} doors / windows to check (hollow markers)`,
                  );
                if (r.aiLf > 0.5)
                  add(
                    "ai",
                    `${fmt0(r.aiLf)} LF typed by the AI from the tags on the drawing`,
                  );
                if (r.openings && (r.openings.ok || r.openings.added))
                  add(
                    "ai",
                    [
                      r.openings.ok &&
                        `${r.openings.ok} openings confirmed by AI`,
                      r.openings.added &&
                        `${r.openings.added} missed ones added`,
                    ]
                      .filter(Boolean)
                      .join(" · "),
                  );
                if (r.wetLf > 0.5)
                  add(
                    "wet",
                    `${fmt0(r.wetLf)} LF of washroom walls on their own assemblies`,
                  );
                if (r.skippedLf > 0.5)
                  add(
                    "info",
                    `${fmt0(r.skippedLf)} LF left out — no traced type tag`,
                  );
                if (r.gridSf > 0.5)
                  add(
                    "info",
                    `${fmt0(r.gridSf)} SF typed from the grid drawn in the room`,
                  );
                if (r.doorsLf > 0.5)
                  add(
                    "info",
                    `${fmt0(r.doorsLf)} LF of doors crossed and deducted`,
                  );
                if (r.openLf > 0.5)
                  add(
                    "info",
                    `Includes ${fmt0(r.openLf)} LF on open-area walls — check`,
                  );
                if (r.aiNote) add("info", r.aiNote);
                if (r.note) add("info", r.note);
                return N;
              };
              const TONE = {
                warn: "bg-amber-400",
                ai: "bg-violet-400",
                wet: "bg-sky-400",
                info: "bg-gray-500",
              };
              const stat = (label, value, sub) => (
                <div className="min-w-0 flex-1 rounded-xl border border-gray-800 bg-gray-900/60 px-4 py-3">
                  <div className="text-[11px] font-medium text-gray-400">
                    {label}
                  </div>
                  <div className="mt-0.5 text-xl font-semibold tabular-nums text-white">
                    {value}
                  </div>
                  {sub && (
                    <div className="text-[11px] text-gray-500">{sub}</div>
                  )}
                </div>
              );
              return (
                <div className="flex min-h-[540px] flex-col">
                  <div className="flex-1 space-y-4 p-5">
                    <div className="flex items-start gap-3 rounded-xl border border-green-700/40 bg-green-900/15 px-4 py-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500/20 text-green-300">
                        <svg
                          viewBox="0 0 16 16"
                          className="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <path d="M3.5 8.5l3 3 6-7" />
                        </svg>
                      </span>
                      <div>
                        <div className="text-[13px] font-semibold text-white">
                          Drew {fmt0(result.shapes)} shapes on{" "}
                          {result.pages.length} sheet
                          {result.pages.length === 1 ? "" : "s"}
                        </div>
                        <p className="text-xs text-gray-400">
                          They're ordinary takeoff shapes now — edit, delete or
                          change heights like any other. The whole trace can be
                          undone from the Assemblies tab.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {stat("Walls", `${fmt0(totLf)} LF`)}
                      {stat("Ceilings", `${fmt0(totSf)} SF`)}
                      {stat(
                        "Doors · windows",
                        `${cnt("door") + cnt("door2")} · ${cnt("window")}`,
                        cnt("door2") ? `incl. ${cnt("door2")} double` : null,
                      )}
                      {stat(
                        "To check",
                        toCheck,
                        cnt("bead")
                          ? `${cnt("bead")} outside corners counted`
                          : null,
                      )}
                    </div>
                    <div className="space-y-3">
                      {result.pages.map((r) => {
                        const notes = notesOf(r);
                        const walls = Object.entries(r.walls || {}).sort(
                          (a, b) => b[1] - a[1],
                        );
                        const ceils = Object.entries(r.ceilings || {}).sort(
                          (a, b) => b[1] - a[1],
                        );
                        const maxW = Math.max(1, ...walls.map((x) => x[1]));
                        const maxC = Math.max(1, ...ceils.map((x) => x[1]));
                        const bars = (rows, max, unit, color) =>
                          rows.length ? (
                            <div className="space-y-1.5">
                              {rows.map(([k, v]) => (
                                <div
                                  key={k}
                                  className="grid grid-cols-[minmax(0,1fr)_72px] items-center gap-2"
                                >
                                  <div className="min-w-0">
                                    <div className="truncate text-[12px] text-gray-200">
                                      {k}
                                    </div>
                                    <div className="mt-0.5 h-1 rounded-full bg-gray-800">
                                      <div
                                        className={`h-1 rounded-full ${color}`}
                                        style={{ width: `${(v / max) * 100}%` }}
                                      />
                                    </div>
                                  </div>
                                  <div className="text-right text-[12px] font-semibold tabular-nums text-white">
                                    {fmt0(v)} {unit}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-[11px] text-gray-600">—</div>
                          );
                        return (
                          <section
                            key={`${r.page}-${r.kind}`}
                            className="rounded-xl border border-gray-800 bg-gray-900/60"
                          >
                            <header className="flex flex-wrap items-center gap-2 border-b border-gray-800 px-4 py-2.5">
                              <span
                                className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${r.kind === "rcp" ? "bg-sky-500/15 text-sky-300" : "bg-orange-500/15 text-orange-300"}`}
                              >
                                {r.kind === "rcp" ? "RCP" : "FLOOR"}
                              </span>
                              <span className="text-[13px] font-semibold text-white">
                                {pageLabel(r.page)}
                              </span>
                              <span className="text-[11px] text-gray-500">
                                {r.rooms} room{r.rooms === 1 ? "" : "s"}
                                {r.kind !== "rcp" && r.height
                                  ? ` · ${fmtFeetIn(r.height)} walls`
                                  : ""}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  onGoToPage(r.page);
                                  onClose();
                                }}
                                className={`ml-auto ${BTN_SECONDARY} !h-7`}
                              >
                                Open sheet
                              </button>
                            </header>
                            <div className="grid gap-4 px-4 py-3 md:grid-cols-[1fr_1fr_1.2fr]">
                              <div>
                                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                                  Walls
                                </div>
                                {bars(walls, maxW, "LF", "bg-orange-500")}
                              </div>
                              <div>
                                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                                  Ceilings
                                </div>
                                {bars(ceils, maxC, "SF", "bg-sky-500")}
                              </div>
                              <div>
                                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                                  Notes
                                </div>
                                {notes.length ? (
                                  <ul className="space-y-1.5">
                                    {notes.map((x, i) => (
                                      <li
                                        key={i}
                                        className="flex gap-2 text-[11px] leading-snug text-gray-300"
                                      >
                                        <span
                                          className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${TONE[x.tone]}`}
                                        />
                                        {x.text}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div className="text-[11px] text-gray-500">
                                    Nothing to check.
                                  </div>
                                )}
                              </div>
                            </div>
                          </section>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-gray-500">
                      Click any line to change its assembly or height, or press
                      Delete to remove it. A room that didn't close can be
                      filled with{" "}
                      <span className="text-gray-300">Fill room</span> or drawn
                      by hand.
                    </p>
                  </div>
                  <WizFooter
                    left={
                      <span className="flex items-center gap-3 text-[11px]">
                        <span className="flex items-center gap-1.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                          to check
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />
                          AI
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                          washrooms
                        </span>
                      </span>
                    }
                  >
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className={BTN_SECONDARY}
                    >
                      Back to options
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className={BTN_PRIMARY}
                    >
                      Done
                    </button>
                  </WizFooter>
                </div>
              );
            })()}
        </div>
      </div>
    </div>
  );
}
